import { checkIsDeliveryRequired, findProduct, generateCartBody, getAvailableDeliveries, getAvailablePaymentMethods, checkPositionsDiscounts } from './utils/utils'
import useCart, { CART_STORAGE_KEY, SESSION } from './hooks/useCart'
import ProfilesQuantityModal from './modals/ProfilesQuantityModal'
import DeliveryPointsModal from './modals/DeliveryPointsModal'
import { useOrderInfo } from 'contexts/OrderInfoContext'
import Checkbox from 'components/inputs/Checkbox'
import LinkToTerms from 'components/LinkToTerms'
import CourierModal from './modals/CourierModal'
import { useNavigate } from 'react-router-dom'
import { displayPrice } from 'utils/strings'
import BlikModal from './modals/BlikModal'
import { removeItem } from 'utils/storage'
import * as fields from './utils/fields'
import * as exp from 'utils/validation'
import React, { useState } from 'react'
import Button from 'components/Button'
import OrderSummary from './Summary'
import CheckItem from './CheckItem'
import { POST } from 'api/request'
import Discount from './Discount'
import * as urls from 'api/urls'
import OrderItem from './Item'

interface OrderFormProps {
  lastOrder?: order.Cart
  checkPrice?: order.CheckPrice
  reloadProducts: (discount_code: string) => void
}

type Errors = Record<string, string>
type Modal = 'quantity' | 'blik' | 'privacy' | 'terms' | Required<order.Cart>['selected_delivery']


const OrderForm: React.FC<OrderFormProps> = ({ checkPrice, lastOrder, reloadProducts }) => {
  const products = checkPrice?.products
  const [cart, setCart, sendCartToGTM] = useCart(checkPrice, lastOrder)
  const [modal, setModal] = useState<Modal | ''>('')
  const [errors, setErrors] = useState<Errors>({})

  const [, , setOrderInfo] = useOrderInfo()

  const isDeliveryRequired = checkIsDeliveryRequired(cart.positions, products)
  const positionsDiscounts = checkPositionsDiscounts(cart.positions, products)
  const deliveries = getAvailableDeliveries(products)
  const additionalProfile = findProduct(products, 'additional_profile')
  const qrBoard = findProduct(products, 'qr_board')

  const navigate = useNavigate()
  const hideModal = () => {
    setModal('')
  }

  const updateForm = (values: Partial<order.Cart>) => {
    setCart(prev => ({ ...prev, ...values }))
  }

  const close = (order_id = '') => {
    sendCartToGTM('purchase', cart, order_id)
    if (order_id) {
      removeItem(SESSION, CART_STORAGE_KEY)
    }
    if (window.location.search.includes('relatedId')) {
      navigate(-1)
    } else {
      if (order_id) setOrderInfo(order_id)
      navigate(`/login${order_id ? `?order_id=${order_id}` : ''}`)
    }
  }

  const realizeTransfer = async () => {
    try {
      const response = await POST<order.OrderResponse>(urls.orders, { body: generateCartBody(cart) })
      if (response.ok && response.data.payment) {
        sendCartToGTM('purchase', cart, response.data.data.id)
        window.open(response.data.payment.url, '_self')
      } else {
        throw new Error('Wrong response')
      }
    } catch (error) {
      console.error(':/')
    }
  }

  const validateForm = () => {
    const isMailOk = exp.final.email.test(cart.email)
    const isPhoneOk = exp.final.phone.test(cart.phone)
    const isDeliveryOk = isDeliveryRequired ? !!cart.selected_delivery : true
    const isNipOk = cart.invoice ? exp.final.nip.test(cart.invoice.nip) : true
    const isInvoiceOk = cart.invoice ? (cart.invoice.company_name && isNipOk) : true
    if (isMailOk && isPhoneOk && isInvoiceOk && cart.privacy && cart.payment_method && isDeliveryOk) {
      if (Object.keys(errors).length > 0) {
        setErrors({})
      }
      if (cart.payment_method === 'blik') {
        sendCartToGTM('begin_checkout')
        setModal('blik')
      } else if (cart.payment_method === 'transfer') {
        sendCartToGTM('begin_checkout')
        realizeTransfer()
      }

    } else {
      const errors: Errors = {}
      if (!cart.name) errors.name = 'Musisz wpisać nazwę'
      if (!isMailOk) errors.email = cart.email ? 'Niewłaściwy adres email' : 'Musisz wpisać adres email'
      if (!isPhoneOk) errors.phone = cart.phone ? 'Niewłaściwy numer telefonu' : 'Musisz wpisać numer telefonu'
      if (!isNipOk) errors.nip = 'Niewłaściwy numer NIP'
      if (!cart.privacy) errors.privacy = 'Musisz wyrazić zgody'
      if (!isDeliveryOk) errors.selected_delivery = 'Musisz wybrać opcję dostawy'
      if (!cart.payment_method) errors.payment_method = 'Musisz wybrać metodę płatności'
      if (cart.invoice && !cart.invoice.company_name) errors.company_name = 'Brakuje nazwy firmy'
      setErrors(errors)
    }
  }

  const payButton = <Button
    disabled={!cart.positions.length}
    type='primary'
    text='Kup i zapłać'
    action={validateForm}
  />

  const summary = cart.positions.length > 0
    ? <OrderSummary
      cart={cart}
      products={products}
      discountCode={checkPrice?.discount_code}
    />
    : null

  const discount = <Discount
    isValid={checkPrice?.discount_code?.valid}
    value={cart.discount_code}
    setValue={discount_code => {
      reloadProducts(discount_code)
      updateForm({ discount_code })
    }}
  />

  return (
    <>
      {modal === 'quantity' && <ProfilesQuantityModal
        onClose={hideModal}
      />}
      {modal === 'blik' && <BlikModal
        url={urls.orders}
        getBody={blik => generateCartBody(cart, blik)}
        onClose={hideModal}
        callback={close}
      />}
      {(modal === 'courier' || modal === 'cash_on_delivery_courier') && <CourierModal
        initialForm={cart.courier || { name: cart.name, phone: cart.phone } as order.Courier}
        onClose={hideModal}
        saveForm={courier => {
          setCart(prev => {
            const { delivery_point, ...rest } = prev
            const newCart = {
              ...rest,
              courier,
              selected_delivery: modal
            }
            if (prev.selected_delivery !== modal) {
              sendCartToGTM('add_shipping_info', newCart)
            }
            return newCart
          })
        }}
      />}
      {modal === 'delivery_point' && <DeliveryPointsModal
        initialZoom={12}
        placeAddress='Warszawa'
        initialCoordinates={[52.2360932, 21.0087777]} // warszawa
        initialServices={['inpost']}
        close={delivery_point => {
          if (delivery_point) {
            setCart(prev => {
              const { courier, ...rest } = prev
              const newCart = {
                ...rest,
                delivery_point,
                selected_delivery: modal
              }
              if (prev.selected_delivery !== modal) {
                sendCartToGTM('add_shipping_info', newCart)
              }
              return newCart
            })
          }
          hideModal()
        }}
      />}

      <div className={`topbar topbar--mobile sticky dark`}>
        <div className='topbar__wrap'>
          <div className='topbar__logo'>
            <h1
              onClick={() => {
                navigate('/')
              }}>
              Moje Kondolencje
            </h1>
          </div>
          <div className="buttons" style={{ display: 'flex', gap: '.5em' }}>
            <button
              className='btn btn--round'
              style={{ backgroundColor: 'var(--on-bg-sec-color)' }}
              onClick={() => {
                navigate('/')
              }}>
              Aplikacja
            </button>
            <button
              className='btn btn--round'
              style={{ backgroundColor: 'var(--on-bg-sec-color)' }}
              onClick={() => {
                window.open('https://mojekondolencje.pl/', '_self')
              }}>
              Sklep
            </button>
          </div>
        </div>
      </div>

      <div className='order'>
        {/* To 'bg' to jest szare tło na desktopie z prawej strony */}
        <div className='bg'></div>
        <div className='order__form'>
          <h2>Dane klienta</h2>
          {fields.basic.map(({ key, placeholder, label }) => <div
            key={key}
            className='myInput'>
            <label className={errors[key] ? 'form__error' : undefined}>{errors[key] || label}</label>
            <input
              type='text'
              className={errors[key] ? 'error' : ''}
              value={cart[key as keyof Pick<order.Cart, 'name' | 'email' | 'phone'>]}
              placeholder={placeholder}
              onChange={e => {
                const { value } = e.target
                if (key === 'name'
                  || (key === 'email' && (!value || exp.initial.email.test(value)))
                  || (key === 'phone' && (!value || exp.initial.phone.test(value)))) {
                  updateForm({ [key]: value })
                }
              }}
            />
          </div>)}

          {/* <h2>Dokument Sprzedaży</h2>

          <div className='chooseDocument'>
            <div
              className='checkItem'
              onClick={() => {
                if (cart.invoice) {
                  setCart(prev => {
                    const { invoice, ...rest } = prev
                    return rest
                  })
                }
              }}>
              <div className={`circle ${!cart.invoice && 'checked'}`}>
                <i className='icon icon--check'></i>
              </div>
              <span>e-Paragon</span>
            </div>
            <div
              className='checkItem'
              onClick={() => {
                if (!cart.invoice) {
                  const invoice: order.Invoice = {
                    nip: '',
                    company_name: '',
                    company_address: ''
                  }
                  updateForm({ invoice })
                }
              }}>
              <div className={`circle ${cart.invoice && 'checked'}`}>
                <i className='icon icon--check'></i>
              </div>
              <span>Faktura VAT</span>
            </div>
          </div>

          <div className='infoDocument'>
            {!cart.invoice
              ? <div className='infoDocument--eParagon'>
                <p>Wybierając e-paragon wyrażasz zgodę na otrzymanie paragonu drogą mailową w formie elektronicznej.</p>
              </div>
              : <div className='infoDocument--fakturaVAT'>
                {fields.invoice.map(({ key, placeholder, label }) => <div
                  key={key}
                  className='myInput'>
                  <label>{errors[key] || label}</label>
                  <input
                    type='text'
                    className={errors[key] ? 'error' : ''}
                    value={cart.invoice?.[key as keyof order.Invoice] || ''}
                    placeholder={placeholder}
                    onChange={e => {
                      const { value } = e.target
                      if (key !== 'nip'
                        || (key === 'nip' && (!value || exp.initial.nip.test(value)))) {
                        setCart(prev => ({ ...prev, invoice: { ...prev.invoice!, [key]: value } }))
                      }
                    }}
                  />
                </div>)}
              </div>}
          </div> */}

          {isDeliveryRequired && <>

            <div className="order__title">
              <h2>Metoda Dostawy</h2>
              {deliveries?.deliveryHtml && <div dangerouslySetInnerHTML={{ __html: deliveries.deliveryHtml }} />}
            </div>
            <div className='delivery'>
              {errors.selected_delivery && <p className='form__error'>{errors.selected_delivery}</p>}
              {deliveries?.list?.map(({ key, name, cost }) => <CheckItem
                key={key}
                name={name}
                price={cost}
                icon={key !== 'none' ? 'inpost' : undefined}
                isSelected={cart.selected_delivery === key}
                additionalText={cart.courier && (key === 'courier' || key === 'cash_on_delivery_courier')
                  ? `${cart.courier?.name} - ${cart.courier?.city}, ${cart.courier?.street} ${cart.courier?.estate_number}${cart.courier?.local_number ? `/${cart.courier?.local_number}` : ''}`
                  : cart.delivery_point && key === 'delivery_point'
                    ? `${cart.delivery_point.name}`
                    : undefined}
                action={() => {
                  if (key === 'none') {
                    if (cart.selected_delivery === 'none') return
                    const { courier, delivery_point, selected_delivery, ...rest } = cart
                    const newCart = { ...rest, selected_delivery: key }
                    setCart(newCart)
                    sendCartToGTM('add_shipping_info', newCart)
                  } else {
                    setModal(key)
                  }
                }}
              />)}
            </div>
          </>}

          <h2>Metoda Płatności</h2>
          <div className='paymentMethod'>
            {errors.payment_method && <p className='form__error'>{errors.payment_method}</p>}
            {getAvailablePaymentMethods(products)?.map(({ name, id }) => <CheckItem
              key={id}
              icon={id}
              name={name}
              isSelected={cart.payment_method === id}
              action={() => {
                if (cart.payment_method !== id) {
                  sendCartToGTM('add_payment_info', { ...cart, payment_method: id })
                  updateForm({ payment_method: id })
                }
              }}
            />)}
          </div>

          {errors.privacy && <p className='form__error'>{errors.privacy}</p>}
          <Checkbox
            checked={cart.privacy}
            onChanged={() => {
              updateForm({ privacy: !cart.privacy })
            }}
            content={<>
              Wyrażam zgodę na przetwarzanie danych zgodnie z <LinkToTerms type='privacy'>polityką prywatności</LinkToTerms> oraz akceptuję <LinkToTerms type='terms'>regulamin sklepu</LinkToTerms>
            </>}
          />

          {/* Ten przycisk ponizej widać na desktopie tylko */}
          {payButton}
        </div>

        <div className='order__details-summary'>
          <div className='order__details'>
            <div className='info'>
              <p>
                Tabliczka z jednym profilem kosztuje {displayPrice(qrBoard?.order_params?.cost || 169.00)},
                każdy kolejny profil do tabliczki to koszt: {displayPrice(additionalProfile?.order_params?.cost || 149.00)}
              </p>
            </div>
            {(qrBoard?.order_params?.cost_last_30_days || additionalProfile?.order_params?.cost_last_30_days) && <div className='info'>
              <p>
                Najniższa cena z ostatnich 30 dni: <br /> Tabliczka z jednym profilem: {displayPrice(qrBoard?.order_params?.cost_last_30_days || 169.00)}, Każdy kolejny profil: {displayPrice(additionalProfile?.order_params?.cost_last_30_days || 149.00)}
              </p>
            </div>}
            <div className='order__cart'>
              {cart.positions.map((position) => <OrderItem
                {...position}
                products={products}
                key={position.cartId}
                discounts={positionsDiscounts}
                setCart={(getState) => {
                  setCart(prev => {
                    const state = typeof getState === 'function' ? getState(prev) : getState
                    const isDeliveryRequired = checkIsDeliveryRequired(state.positions, products)
                    const newCart: order.Cart = isDeliveryRequired
                      ? state
                      : {
                        ...state,
                        courier: undefined,
                        delivery_point: undefined,
                        selected_delivery: 'none'
                      }
                    sendCartToGTM('add_to_cart', newCart)
                    return newCart
                  })
                }}
              />)}

              <div
                className='order__item --button'
                onClick={() => {
                  if (!products) return
                  if (qrBoard) {
                    const newPosition: order.Position = {
                      cartId: crypto.randomUUID(),
                      id: qrBoard.product_id,
                      amount: 1,
                      addons: {}
                    }
                    setCart(prev => {
                      const newCart = {
                        ...prev,
                        positions: [...prev.positions, newPosition]
                      }
                      sendCartToGTM('add_to_cart', newCart)
                      return newCart
                    })
                  }
                }}>
                {(() => {
                  let text = '+ Dodaj kolejną tabliczkę'
                  const qrBoardId = qrBoard?.product_id
                  if (qrBoardId && positionsDiscounts[qrBoardId]?.next) {
                    text += ` (${qrBoard.discounts.all_items ? 'rabat' : 'kolejna'} -${positionsDiscounts[qrBoardId].next} %)`
                  }
                  return text
                })()}
              </div>
            </div>
          </div>

          {/* To podsumowanie znika na telefonie, jest na desktopie */}
          <div className='order__summary'>
            <h2>Podsumowanie</h2>
            {discount}
            {summary}
          </div>

        </div>

        {/* To samo co wyzej, ale wyświetla się w dobrym miejscu na telefonie. To wyzej znika z telefonu */}
        <div className='order__summaryMobile'>
          <h2>Podsumowanie</h2>
          {discount}
          {summary}
          {payButton}
        </div>
      </div>
    </>)
}

export default OrderForm